import "../styles/chatbot.css";
import ChatBotContainer from "../components/ChatBotContainer";
import SystemContextForm from "../components/SystemContextForm";
import { loadSystemContext } from "../utils/systemContext";
import { Sun, Moon, LogOut, Bot, Menu, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import CHAT_MODES from "../constants/modes";
import { chatModesMap as staticChatModesMap } from "../config/chatModesMap";
import { logout } from "../store/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { APP_ROUTES } from "../constants";
import { useAppSelector } from "../hooks/redux";
import instanceService from "../services/instanceService";
import { LoadingSpinner } from "../components/common/ui/LoadingSpinner";
import { AcknowledgmentModal } from "../components/common/ui/AcknowledgmentModal";

function ThemeToggle({ isDark, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`p-1.5 rounded-full transition-colors ${
        isDark
          ? "bg-gray-800 text-yellow-400 hover:bg-gray-700"
          : "bg-gray-200 text-gray-800 hover:bg-gray-300"
      }`}
      aria-label="Toggle theme"
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {isDark ? (
        <Sun size={20} />
      ) : (
        <Moon size={20} className="text-gray-500" />
      )}
    </button>
  );
}

function Header({ isDark, onThemeToggle }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
    navigate(APP_ROUTES.LOGIN);
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm fixed top-0 left-0 right-0 h-14 z-50 transition-colors">
      <div className="flex items-center h-full px-4 md:px-6 max-w-screen-2xl mx-auto">
        <div className="flex items-center gap-3">
          <span className="text-lg font-semibold text-gray-800 dark:text-white">
            Welcome to HDX AI 
          </span>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <div className="flex items-center gap-1">
            <ThemeToggle isDark={isDark} onToggle={onThemeToggle} />
            <button
              onClick={handleLogout}
              className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
              role="menuitem"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function Chat() {
  const [, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatModesMap, setChatModesMap] = useState({});
  const { user } = useAppSelector((state) => state.auth);
  const [activeMode, setActiveMode] = useState("");
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("theme") === "dark" ||
        (!localStorage.getItem("theme") &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      );
    }
    return false;
  });
  const [systemContext, setSystemContext] = useState({
    companyCode: "",
    regionCode: "",
    userCode: ""
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (isDarkTheme) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkTheme]);

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  const fetchInstances = useCallback(async () => {
    setLoading(true);
    try {
      // Use static chat modes configuration
      buildChatModesMap();

      // Set the first available mode as active (HI_BRO, HI_CRM, or UNIFIED)
      const availableModes = Object.keys(staticChatModesMap);
      if (availableModes.length > 0) {
        // Prioritize the new instances: HI_BRO, HI_CRM, UNIFIED
        const priorityModes = [
          CHAT_MODES.TYPE.HI_CRM,
          CHAT_MODES.TYPE.HI_BRO,
          CHAT_MODES.TYPE.UNIFIED,
        ];
        const firstAvailableMode =
          priorityModes.find((mode) => availableModes.includes(mode)) ||
          availableModes[0];
        setActiveMode(firstAvailableMode);
      }
    } catch (error) {
      console.error("Error loading chat modes:", error);
    } finally {
      setLoading(false);
    }
  }, []);
  const buildChatModesMap = () => {
    // Use the static chatModesMap configuration
    setChatModesMap(staticChatModesMap);
  };

  const handleSystemContextChange = useCallback((newContext) => {
    setSystemContext(newContext);
    const modalMessage = "System context updated successfully!";
    setModalMessage(modalMessage);
    setShowModal(true);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed(prev => !prev);
  }, []);

  useEffect(() => {
    fetchInstances();
  }, []);

  useEffect(() => {
    const savedContext = loadSystemContext();
    setSystemContext(savedContext);
  }, []);

  return (
    <div className="w-full h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 transition-colors">
      <Header isDark={isDarkTheme} onThemeToggle={toggleTheme} />
      {loading ? (
        <LoadingSpinner className="h-64" />
      ) : (
        <div className="flex h-[calc(100vh-3.5rem)] mt-14">
          {/* Sidebar Toggle Button */}
          <button
            onClick={toggleSidebar}
            className="fixed top-16 left-2 z-50 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors md:hidden"
            title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          >
            {isSidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>

          {/* Collapsible Sidebar */}
          <aside className={`
            hidden md:flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 
            fixed h-[calc(100vh-3.5rem)] transition-all duration-300 ease-in-out
            ${isSidebarCollapsed ? 'w-16' : 'w-60'}
          `}>
            {/* Sidebar Header with Toggle */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                {!isSidebarCollapsed && (
                  <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Chat Modes
                  </h2>
                )}
                <button
                  onClick={toggleSidebar}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  {isSidebarCollapsed ? <Menu size={16} /> : <ChevronLeft size={16} />}
                </button>
              </div>
            </div>

            {/* Chat Modes Navigation */}
            <div className="p-3">
              <nav className="space-y-1">
                {Object.entries(chatModesMap).map(
                  ([modeKey, { icon, label }]) => {
                    const isActive = activeMode === modeKey;
                    return (
                      <button
                        key={modeKey}
                        onClick={() => setActiveMode(modeKey)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors
                          ${
                            isActive
                              ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/50 font-medium"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        title={isSidebarCollapsed ? label : undefined}
                      >
                        {icon}
                        {!isSidebarCollapsed && <span>{label}</span>}
                      </button>
                    );
                  }
                )}
              </nav>
            </div>

            {/* System Context Form */}
            <div className="mt-auto">
              <SystemContextForm 
                onCodesChange={handleSystemContextChange} 
                isCollapsed={isSidebarCollapsed}
              />
            </div>
          </aside>

          {/* Main Content */}
          <main className={`flex-1 bg-white h-[calc(100vh-3.5rem)] transition-all duration-300 ease-in-out ${
            isSidebarCollapsed ? 'md:ml-16' : 'md:ml-60'
          }`}>
            {activeMode && Object.keys(chatModesMap).length > 0 && (
              <ChatBotContainer
                initialMode={activeMode}
                chatModesMap={chatModesMap}
                companyCode={systemContext.companyCode}
                regionCode={systemContext.regionCode}
                userCode={systemContext.userCode}
              />
            )}
          </main>
        </div>
      )}
      <AcknowledgmentModal
        isOpen={showModal}
        message={modalMessage}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
}

export default Chat;
